import { useEffect, useRef } from 'react';
import { getWorkspaceId } from './api';

type Listener = (event: string, data: any) => void;
const listeners = new Set<Listener>();
let socket: WebSocket | null = null;
let currentWs: string | null = null;
let retry = 0;
let timer: number | undefined;
let pinger: number | undefined;
export type RtStatus = 'connecting' | 'open' | 'closed';
let status: RtStatus = 'closed';
const statusListeners = new Set<(s: RtStatus) => void>();
const setStatus = (s: RtStatus) => { status = s; statusListeners.forEach((f) => f(s)); };

function connect(ws: string) {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  setStatus('connecting');
  const sock = new WebSocket(`${proto}://${location.host}/api/v1/ws?workspace_id=${ws}`);
  socket = sock;
  sock.onopen = () => {
    retry = 0; setStatus('open');
    pinger = window.setInterval(() => sock.readyState === 1 && sock.send('ping'), 25000);
  };
  sock.onmessage = (e) => {
    try { const m = JSON.parse(e.data); if (m.event !== 'pong') listeners.forEach((l) => l(m.event, m.data)); } catch { /* ignore malformed */ }
  };
  sock.onclose = (e) => {
    window.clearInterval(pinger);
    setStatus('closed');
    if (socket !== sock || e.code === 4401 || e.code === 4403) return;
    retry = Math.min(retry + 1, 6);
    timer = window.setTimeout(() => currentWs && connect(currentWs), 500 * 2 ** retry + Math.random() * 500);
  };
}

/** Opens (or switches) the workspace socket. Reconnects with jittered exponential backoff. */
export function startRealtime(ws: string | null) {
  if (ws === currentWs && socket && socket.readyState <= 1) return;
  currentWs = ws;
  window.clearTimeout(timer);
  const old = socket; socket = null; old?.close();
  if (ws) connect(ws);
}

export function useRealtime(fn: Listener, deps: unknown[] = []) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const l: Listener = (e, d) => ref.current(e, d);
    listeners.add(l);
    return () => { listeners.delete(l); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function onRealtimeStatus(fn: (s: RtStatus) => void) { statusListeners.add(fn); fn(status); return () => { statusListeners.delete(fn); }; }
export const realtimeWorkspace = () => currentWs ?? getWorkspaceId();
